import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { UserPlus, Trash2, Mail, Crown, Shield, Pen, Edit3, User, Copy, Check } from 'lucide-react'
import { supabase } from '@/infrastructure/supabase/client'
import { useAuthStore } from '@/store/authStore'
import { usePermissions } from '@/hooks/usePermissions'
import type { UserRole, InviteRole } from '@brix/types'

const SUPABASE_URL = import.meta.env['VITE_SUPABASE_URL'] as string
const DASHBOARD_URL = import.meta.env['VITE_DASHBOARD_URL'] as string ?? window.location.origin

const ROLE_META: Record<UserRole, { label: string; desc: string; icon: typeof Crown; color: string }> = {
  owner:  { label: 'Owner',  desc: 'Full control, including billing and deleting the workspace', icon: Crown,  color: 'text-violet-600 bg-violet-50' },
  admin:  { label: 'Admin',  desc: 'Manages sites, pages, blog, and team members',              icon: Shield, color: 'text-blue-600 bg-blue-50' },
  editor: { label: 'Editor', desc: 'Can create, edit, and delete any blog post',               icon: Pen,    color: 'text-green-600 bg-green-50' },
  author: { label: 'Author', desc: 'Can only create and edit their own posts',                  icon: Edit3,  color: 'text-amber-600 bg-amber-50' },
  member: { label: 'Member', desc: 'Read-only access to the dashboard',                         icon: User,   color: 'text-gray-600 bg-gray-50' },
}

const INVITE_ROLES: InviteRole[] = ['admin', 'editor', 'author', 'member']

interface Member {
  id: string
  role: UserRole
  name: string | null
  email: string
  created_at: string
}

interface Invite {
  id: string
  email: string
  role: InviteRole
  expires_at: string
  created_at: string
}

function useTeam() {
  const { tenant } = useAuthStore()
  return useQuery({
    queryKey: ['team', tenant?.id],
    enabled: !!tenant,
    queryFn: async () => {
      const { data: members, error: mErr } = await supabase
        .rpc('get_tenant_members_with_email')
      if (mErr) throw mErr

      const { data: invites, error: iErr } = await supabase
        .from('tenant_invites')
        .select('id, email, role, expires_at, created_at')
        .order('created_at', { ascending: false })
      if (iErr) throw iErr

      return {
        members: (members ?? []) as Member[],
        invites: (invites ?? []) as Invite[],
      }
    },
  })
}

export function TeamPage() {
  const qc = useQueryClient()
  const { tenant } = useAuthStore()
  const perms = usePermissions()
  const { data, isLoading } = useTeam()

  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<InviteRole>('editor')
  const [inviting, setInviting] = useState(false)
  const [inviteError, setInviteError] = useState<string | null>(null)
  const [inviteLink, setInviteLink] = useState<string | null>(null)
  const [inviteEmailSent, setInviteEmailSent] = useState<boolean | null>(null)
  const [copied, setCopied] = useState(false)

  const invite = async () => {
    if (!inviteEmail.trim() || !tenant) return
    setInviting(true); setInviteError(null); setInviteLink(null); setInviteEmailSent(null)
    const { data: inserted, error } = await supabase
      .from('tenant_invites')
      .insert({ tenant_id: tenant.id, email: inviteEmail.trim().toLowerCase(), role: inviteRole })
      .select('id, token')
      .single()
    if (error) { setInviteError(error.message); setInviting(false); return }

    // Send invite email
    const { data: { session } } = await supabase.auth.getSession()
    const res = await fetch(`${SUPABASE_URL}/functions/v1/invite-send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
      body: JSON.stringify({ inviteId: inserted.id }),
    })
    setInviting(false)
    const link = `${DASHBOARD_URL}/invite/${inserted.token}`
    if (!res.ok) {
      setInviteEmailSent(false)
      setInviteLink(link)
    } else {
      setInviteEmailSent(true)
      const result = await res.json() as { acceptUrl?: string }
      setInviteLink(result.acceptUrl ?? link)
    }
    setInviteEmail('')
    void qc.invalidateQueries({ queryKey: ['team'] })
  }

  const copyLink = (link: string) => {
    void navigator.clipboard.writeText(link)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const removeInvite = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('tenant_invites').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team'] }),
  })

  const removeMember = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('users').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team'] }),
  })

  if (isLoading) return <div className="flex h-64 items-center justify-center"><div className="h-6 w-6 animate-spin rounded-full border-2 border-violet-600 border-t-transparent" /></div>

  return (
    <div className="p-8 max-w-3xl">
      <h1 className="mb-1 text-2xl font-bold text-gray-900">Team</h1>
      <p className="mb-8 text-sm text-gray-500">Manage members and their workspace access.</p>

      {/* Role reference */}
      <div className="mb-8 rounded-xl border border-gray-200 bg-white overflow-hidden">
        <div className="border-b border-gray-100 px-5 py-3">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500">Available roles</h2>
        </div>
        <div className="divide-y divide-gray-50">
          {(Object.entries(ROLE_META) as [UserRole, (typeof ROLE_META)[UserRole]][]).map(([role, meta]) => {
            const Icon = meta.icon
            return (
              <div key={role} className="flex items-start gap-3 px-5 py-3">
                <div className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${meta.color}`}>
                  <Icon className="h-3.5 w-3.5" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">{meta.label}</p>
                  <p className="text-xs text-gray-500">{meta.desc}</p>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Members */}
      <div className="mb-8 rounded-xl border border-gray-200 bg-white overflow-hidden">
        <div className="border-b border-gray-100 px-5 py-3 flex items-center justify-between">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
            Active members ({data?.members.length ?? 0})
          </h2>
        </div>
        <div className="divide-y divide-gray-50">
          {data?.members.map(m => {
            const meta = ROLE_META[m.role]
            const Icon = meta.icon
            return (
              <div key={m.id} className="flex items-center gap-3 px-5 py-3">
                <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${meta.color}`}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{m.name ?? m.email}</p>
                  <p className="text-xs text-gray-400">{meta.label}</p>
                </div>
                {perms.canAccessTeam && m.role !== 'owner' && (
                  <button
                    onClick={() => removeMember.mutate(m.id)}
                    className="rounded-lg p-1.5 text-gray-300 hover:bg-red-50 hover:text-red-500 transition-colors"
                    title="Remove member"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Pending invites */}
      {(data?.invites.length ?? 0) > 0 && (
        <div className="mb-8 rounded-xl border border-gray-200 bg-white overflow-hidden">
          <div className="border-b border-gray-100 px-5 py-3">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              Pending invitations ({data?.invites.length})
            </h2>
          </div>
          <div className="divide-y divide-gray-50">
            {data?.invites.map(inv => (
              <div key={inv.id} className="flex items-center gap-3 px-5 py-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-50 text-amber-500">
                  <Mail className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{inv.email}</p>
                  <p className="text-xs text-gray-400">
                    {ROLE_META[inv.role as UserRole]?.label ?? inv.role} · expires {new Date(inv.expires_at).toLocaleDateString()}
                  </p>
                </div>
                {perms.canAccessTeam && (
                  <button
                    onClick={() => removeInvite.mutate(inv.id)}
                    className="rounded-lg p-1.5 text-gray-300 hover:bg-red-50 hover:text-red-500 transition-colors"
                    title="Cancel invite"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Invite form */}
      {perms.canAccessTeam && (
        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <h2 className="mb-4 text-sm font-semibold text-gray-900 flex items-center gap-2">
            <UserPlus className="h-4 w-4 text-violet-600" />
            Invite someone
          </h2>
          <div className="flex gap-2">
            <input
              type="email"
              aria-label="Invite email"
              placeholder="email@company.com"
              value={inviteEmail}
              onChange={e => { setInviteEmail(e.target.value); setInviteError(null); setInviteLink(null) }}
              onKeyDown={e => e.key === 'Enter' && void invite()}
              className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
            <select
              value={inviteRole}
              onChange={e => setInviteRole(e.target.value as InviteRole)}
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
            >
              {INVITE_ROLES.map(r => (
                <option key={r} value={r}>{ROLE_META[r]?.label ?? r}</option>
              ))}
            </select>
            <button
              onClick={() => void invite()}
              disabled={inviting || !inviteEmail.trim()}
              className="flex items-center gap-2 rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50 transition-colors"
            >
              {inviting ? <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" /> : <UserPlus className="h-3.5 w-3.5" />}
              Invite
            </button>
          </div>
          {inviteError && <p className="mt-2 text-xs text-red-500">{inviteError}</p>}
          {inviteLink && (
            <div className={`mt-3 rounded-lg border px-3 py-2.5 ${inviteEmailSent === false ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
              <p className={`text-xs font-semibold mb-1.5 ${inviteEmailSent === false ? 'text-amber-700' : 'text-green-700'}`}>
                {inviteEmailSent === false
                  ? 'Could not send invite email — share this link manually:'
                  : 'Invite sent. Share this link as a backup:'}
              </p>
              <div className="flex items-center gap-2">
                <code className={`flex-1 truncate text-xs font-mono rounded px-2 py-1 bg-white border ${inviteEmailSent === false ? 'text-amber-800 border-amber-200' : 'text-green-800 border-green-200'}`}>
                  {inviteLink}
                </code>
                <button
                  onClick={() => copyLink(inviteLink)}
                  className={`flex items-center gap-1 rounded-lg border bg-white px-2 py-1 text-xs font-medium transition-colors ${inviteEmailSent === false ? 'border-amber-300 text-amber-700 hover:bg-amber-50' : 'border-green-300 text-green-700 hover:bg-green-50'}`}
                >
                  {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>
          )}
          <p className="mt-3 text-xs text-gray-400">
            An invite email will be sent via Resend. Copy the link as a fallback in case they don't receive it.
          </p>
        </div>
      )}
    </div>
  )
}
